import axios from 'axios';
import aiImagesApi from 'common/src/modules/aiImages/aiImages.api';
import getActiveComp from 'common/src/modules/creativeTemplates/copilot/agents/adBuilderAgent/utils/getActiveComp';
import {TemplateAssetType} from 'common/src/modules/dynamicParameters/dynamicParameters.enums';
import {TemplateAsset} from 'common/src/modules/dynamicParameters/dynamicParameters.types';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {AdBuilderAgentToolName} from '../adBuilderAgent.enums';

const inputSchema = z.object({
  assetId: z.string().describe('The ID of the image asset to extract text layers from'),
  mergeLevel: z.number().nullable().optional().describe('Not in use at the moment (0.0 to 1.0)'),
});

function extractTextLayersFromImageTool(store: CLStore) {
  return tool({
    name: AdBuilderAgentToolName.ExtractTextLayersFromImage,
    displayName: 'Extracting text layers from image',
    description: 'Extracts text layers and their properties from an image returning a JSON object',
    inputSchema,
    execute: async ({input}: ToolExecuteParams<typeof inputSchema>) => {
      const state = store.getState();

      const activeComp = getActiveComp(store);

      if (!activeComp) {
        return toolResult('ERROR: No active composition found');
      }

      try {
        // Find the template asset by ID from store
        const templateAssets = state.creativeTemplatePage.movieAssets.templateAssets;
        const asset = templateAssets.find((a: TemplateAsset) => a.id === input.assetId);

        if (!asset) {
          return toolResult(`ERROR: Asset with ID "${input.assetId}" not found in template assets`);
        }

        // Check if the asset is an image
        if (asset.metadata.type !== TemplateAssetType.Image) {
          return toolResult(
            `ERROR: Asset "${asset.metadata.name}" is not an image (type: ${asset.metadata.type})`,
          );
        }

        // Call the AI Images API to extract text layers from URL
        const result = await aiImagesApi.extractTextLayersFromImageUrl({
          imageUrl: asset.metadata.url,
          mergeLevel: input.mergeLevel,
          vendorService: 15, // GoogleDocumentAIExtractTextLayers - you may need to adjust this number
          cancelToken: axios.CancelToken.source().token,
        });

        return toolResult(JSON.stringify(result, null, 2));
      } catch (error) {
        return toolResult(
          `ERROR: ${error instanceof Error ? error.message : 'Failed to extract text layers from image'}`,
        );
      }
    },
  });
}

export default extractTextLayersFromImageTool;
